import { OrbitControls } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { useRef } from 'react'
import styled from 'styled-components'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { Lights } from '@/components/VRMViewerV2/components/Lights'
import { RenderFrameLoop } from '@/components/VRMViewerV2/RenderFrameLoop'
import { resolveRenderingQuality } from '@/components/VRMViewerV2/renderingQuality'
import settings from '@/components/VRMViewerV2/settings'
import type { VRMViewerV2Props } from '@/components/VRMViewerV2/types'
import { VRMModel } from './VRMModel'
import { WebglDiagnostics } from './WebglDiagnostics'

const ViewerContainer = styled.div`
  position: fixed;
  top: env(safe-area-inset-top);
  right: 0;
  bottom: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  z-index: 3;
`

export const VRMViewerV2 = ({
  avatarFile,
  motionSequence,
  cammeraControls,
  debug,
  quality,
  onAvatarLoadResult,
}: VRMViewerV2Props) => {
  const orbitControlsRef = useRef<OrbitControlsImpl>(null)
  const renderQuality = resolveRenderingQuality(quality)
  const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1

  return (
    <ViewerContainer>
      <Canvas
        key={renderQuality.antialias ? 'antialias-on' : 'antialias-off'}
        orthographic
        camera={{ zoom: 400, near: 0.1, far: 300, position: [0, 0, 0] }}
        flat
        dpr={Math.min(devicePixelRatio, renderQuality.pixelRatio)}
        gl={{ antialias: renderQuality.antialias }}
        frameloop="never"
      >
        <RenderFrameLoop frameRateCap={renderQuality.frameRateCap} />
        <WebglDiagnostics />
        <Lights />
        {debug?.showGridHelper && <gridHelper />}
        <VRMModel
          {...{ avatarFile, motionSequence, controlsRef: orbitControlsRef }}
          onLoadResult={onAvatarLoadResult}
        />
        <OrbitControls ref={orbitControlsRef} {...settings.orbitControls} {...cammeraControls} />
      </Canvas>
    </ViewerContainer>
  )
}
